import { useState, useEffect, useCallback } from "react";
import { useMutation } from "@apollo/client/react";
import { Loader2, Save, Pencil, Code } from "lucide-react";
import { toast } from "sonner";

import { SAVE_EXERCISE_FILE } from "@/graphql/mutations/admin";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MathContent } from "@/components/math/math-content";

interface ExerciseEditDialogProps {
  filePath: string | null;
  /** Initial exercise JSON fields — used to populate the form */
  initialData?: {
    question?: string;
    answer?: string;
    hint?: string;
    choices?: string[];
    alternative_answers?: string[];
    answer_type?: string;
  } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (filePath: string) => void;
}

function buildJson(fields: {
  question: string;
  answer: string;
  hint: string;
  choices: string;
  alternativeAnswers: string;
  answerType: string;
}): Record<string, unknown> {
  const obj: Record<string, unknown> = {
    question: fields.question,
    answer: fields.answer,
  };
  if (fields.hint.trim()) obj.hint = fields.hint.trim();
  if (fields.answerType.trim()) obj.answer_type = fields.answerType.trim();

  const choicesArr = fields.choices
    .split("\n")
    .map((c) => c.trim())
    .filter(Boolean);
  if (choicesArr.length > 0) obj.choices = choicesArr;

  const altArr = fields.alternativeAnswers
    .split("\n")
    .map((a) => a.trim())
    .filter(Boolean);
  if (altArr.length > 0) obj.alternative_answers = altArr;

  return obj;
}

function fieldsFromJson(json: Record<string, unknown>) {
  return {
    question: (json.question as string) || "",
    answer: (json.answer as string) || "",
    hint: (json.hint as string) || "",
    choices: Array.isArray(json.choices) ? json.choices.join("\n") : "",
    alternativeAnswers: Array.isArray(json.alternative_answers)
      ? json.alternative_answers.join("\n")
      : "",
    answerType: (json.answer_type as string) || "",
  };
}

export function ExerciseEditDialog({
  filePath,
  initialData,
  open,
  onOpenChange,
  onSaved,
}: ExerciseEditDialogProps) {
  const [saveExerciseFile, { loading: saving }] =
    useMutation(SAVE_EXERCISE_FILE);

  const [tab, setTab] = useState<"rich" | "json">("rich");

  // Rich editor fields
  const [question, setQuestion] = useState("");
  const [answer, setAnswer] = useState("");
  const [hint, setHint] = useState("");
  const [choices, setChoices] = useState("");
  const [alternativeAnswers, setAlternativeAnswers] = useState("");
  const [answerType, setAnswerType] = useState("");

  // JSON editor
  const [jsonText, setJsonText] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Populate from initialData when dialog opens
  useEffect(() => {
    if (!open || !initialData) return;
    const fields = fieldsFromJson(initialData as Record<string, unknown>);
    setQuestion(fields.question);
    setAnswer(fields.answer);
    setHint(fields.hint);
    setChoices(fields.choices);
    setAlternativeAnswers(fields.alternativeAnswers);
    setAnswerType(fields.answerType);
    setJsonText(JSON.stringify(initialData, null, 2));
    setJsonError(null);
    setTab("rich");
  }, [open, initialData]);

  // Sync: when switching tabs, transfer data
  const syncToJson = useCallback(() => {
    const obj = buildJson({ question, answer, hint, choices, alternativeAnswers, answerType });
    setJsonText(JSON.stringify(obj, null, 2));
    setJsonError(null);
  }, [question, answer, hint, choices, alternativeAnswers, answerType]);

  const syncToRich = useCallback(() => {
    try {
      const parsed = JSON.parse(jsonText);
      const fields = fieldsFromJson(parsed);
      setQuestion(fields.question);
      setAnswer(fields.answer);
      setHint(fields.hint);
      setChoices(fields.choices);
      setAlternativeAnswers(fields.alternativeAnswers);
      setAnswerType(fields.answerType);
      setJsonError(null);
    } catch {
      // Don't sync if JSON is invalid — user will see the error
    }
  }, [jsonText]);

  const handleTabChange = (newTab: "rich" | "json") => {
    if (newTab === "json" && tab === "rich") syncToJson();
    if (newTab === "rich" && tab === "json") syncToRich();
    setTab(newTab);
  };

  const handleJsonChange = (value: string) => {
    setJsonText(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch (e: unknown) {
      setJsonError(e instanceof Error ? e.message : "Neteisingas JSON");
    }
  };

  const handleSave = async () => {
    if (!filePath) return;

    let jsonContent: string;
    if (tab === "json") {
      if (jsonError) {
        toast.error("Pataisykite JSON klaidas prieš išsaugant");
        return;
      }
      jsonContent = jsonText;
    } else {
      const obj = buildJson({ question, answer, hint, choices, alternativeAnswers, answerType });
      jsonContent = JSON.stringify(obj, null, 2);
    }

    try {
      const { data } = await saveExerciseFile({
        variables: { filePath, jsonContent },
      });
      if (data?.saveExerciseFile?.success) {
        toast.success("Išsaugota");
        onOpenChange(false);
        onSaved?.(filePath);
      } else {
        toast.error(data?.saveExerciseFile?.error || "Klaida");
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : "Klaida");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Pencil className="h-4 w-4" />
            Redaguoti užduotį
          </DialogTitle>
          {filePath && (
            <div className="font-mono text-xs text-muted-foreground select-all">
              {filePath}
            </div>
          )}
        </DialogHeader>

        {/* Tab switcher */}
        <div className="flex rounded-md border overflow-hidden w-fit">
          <button
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${
              tab === "rich"
                ? "bg-primary text-primary-foreground"
                : "bg-muted/50 text-muted-foreground hover:bg-muted"
            }`}
            onClick={() => handleTabChange("rich")}
          >
            <Pencil className="h-3 w-3" />
            Redaktorius
          </button>
          <button
            className={`flex items-center gap-1 px-3 py-1.5 text-xs font-medium transition-colors ${
              tab === "json"
                ? "bg-primary text-primary-foreground"
                : "bg-muted/50 text-muted-foreground hover:bg-muted"
            }`}
            onClick={() => handleTabChange("json")}
          >
            <Code className="h-3 w-3" />
            JSON
          </button>
        </div>

        {/* Rich editor */}
        {tab === "rich" && (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Klausimas (HTML)</Label>
              <Textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                rows={5}
                className="font-mono text-sm"
                placeholder="Klausimo tekstas (galima naudoti HTML)..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Atsakymas</Label>
                <Input
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  placeholder="Teisingas atsakymas"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Atsakymo tipas</Label>
                <Select value={answerType || "_none"} onValueChange={(v) => setAnswerType(v === "_none" ? "" : v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Pasirinkti..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Įprastas (tekstas)</SelectItem>
                    <SelectItem value="fill_blanks">Fill blanks (užpildyk tarpus)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Užuomina</Label>
              <Input
                value={hint}
                onChange={(e) => setHint(e.target.value)}
                placeholder="Pagalbinis tekstas (neprivaloma)"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Pasirinkimai (po vieną eilutėje)</Label>
              <Textarea
                value={choices}
                onChange={(e) => setChoices(e.target.value)}
                rows={3}
                className="text-sm"
                placeholder={"A) pirmas\nB) antras\nC) trecias"}
              />
            </div>

            <div className="space-y-1.5">
              <Label>Alternatyvūs atsakymai (po vieną eilutėje)</Label>
              <Textarea
                value={alternativeAnswers}
                onChange={(e) => setAlternativeAnswers(e.target.value)}
                rows={2}
                className="text-sm"
                placeholder="Kiti priimtini atsakymai (neprivaloma)"
              />
            </div>

            {/* Exercise preview */}
            <ExercisePreview
              question={question}
              answer={answer}
              answerType={answerType}
              hint={hint}
              choices={choices}
            />
          </div>
        )}

        {/* JSON editor */}
        {tab === "json" && (
          <div className="space-y-2">
            <Textarea
              value={jsonText}
              onChange={(e) => handleJsonChange(e.target.value)}
              rows={18}
              className="font-mono text-sm"
              placeholder='{"question": "...", "answer": "..."}'
            />
            {jsonError && (
              <div className="text-xs text-red-600 bg-red-50 dark:bg-red-950 dark:text-red-400 rounded px-3 py-1.5">
                {jsonError}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Atšaukti
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || (tab === "json" && !!jsonError)}
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <Save className="h-4 w-4 mr-1" />
            )}
            Išsaugoti
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExercisePreview({
  question,
  answer,
  answerType,
  hint,
  choices,
}: {
  question: string;
  answer: string;
  answerType: string;
  hint: string;
  choices: string;
}) {
  if (!question && !answer) return null;

  const choicesArr = choices
    .split("\n")
    .map((c) => c.trim())
    .filter(Boolean);

  return (
    <div className="border rounded-lg overflow-hidden">
      <div className="bg-muted/40 px-3 py-1.5 text-[10px] text-muted-foreground uppercase tracking-wide font-medium border-b">
        Peržiūra
      </div>
      <div className="p-4 space-y-3">
        {/* Question */}
        {question && (
          <MathContent
            html={question}
            className="text-sm [&_p]:mb-1 [&_svg]:inline-block [&_svg]:max-w-full [&_img]:max-w-[140px] [&_img]:h-auto [&_img]:rounded [&_img]:align-middle"
          />
        )}

        {/* Choices */}
        {choicesArr.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {choicesArr.map((c, i) => (
              <Badge
                key={i}
                variant="outline"
                className={`text-sm px-3 py-1 ${
                  c === answer || c.replace(/^[A-Z]\)\s*/, "") === answer
                    ? "bg-green-50 border-green-300 text-green-700 dark:bg-green-950 dark:border-green-700 dark:text-green-300"
                    : ""
                }`}
              >
                <MathContent html={c} className="inline" />
              </Badge>
            ))}
          </div>
        )}

        {/* Answer */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <strong>Atsakymas:</strong>
          <MathContent html={answer} className="inline bg-muted px-1.5 py-0.5 rounded text-foreground" />
          {answerType && (
            <Badge variant="outline" className="text-[10px]">{answerType}</Badge>
          )}
        </div>

        {/* Hint */}
        {hint && (
          <div className="text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-950/30 rounded px-3 py-1.5">
            <strong>Užuomina:</strong> {hint}
          </div>
        )}
      </div>
    </div>
  );
}
